import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Receipt, BookOpen, GraduationCap, CreditCard, FileText, Calculator,
  Mail, Phone, Wallet,
} from 'lucide-react';
import PortalTopBar from '../../components/academy/PortalTopBar';
import { useAuth } from '../../context/AuthContext';

function initials(name) {
  return (name || '').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

/* Parent dashboard: lists each linked student and their enrollments + invoices.
 * Adds parent-friendly Finance actions (Make Payment / Request Invoice / Estimate)
 * and the assigned counselor info — parents typically pay tuition in installments
 * so finance access is the primary value of this page. */
export default function ParentDashboard() {
  const { user, api } = useAuth();
  const [children, setChildren] = useState([]);
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/api/academy/my/children')
        .then(({ data }) => setChildren(data.children || []))
        .catch((err) => setError(err.response?.data?.detail || err.message)),
      api.get('/api/academy/my/counselors')
        .then(({ data }) => setCounselors(data.rows || []))
        .catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <PortalTopBar portalLabel="PARENT ACCESS" />
      <main className="max-w-[1200px] mx-auto px-4 lg:px-6 py-8">
        <h1 className="text-[28px] font-extrabold text-[#1D1D1F] mb-1">Welcome, {user?.name}</h1>
        <p className="text-[14px] text-[#6E6E73] mb-6">Monitor your child's enrolled courses and billing.</p>

        {/* Quick finance actions — top-of-page since parents come here mainly to pay */}
        <div className="rounded-2xl border border-[#E5E5E7] bg-white p-5 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3"
          data-testid="parent-finance-actions">
          <ParentActionTile
            icon={CreditCard}
            label="Make Payment"
            hint="Pay your child's installment securely."
            to="/academy/programs"
            testId="parent-action-make-payment"
            primary
          />
          <ParentActionTile
            icon={FileText}
            label="Request Invoice"
            hint="Email me an invoice for this month."
            to="/academy/finance"
            testId="parent-action-request-invoice"
          />
          <ParentActionTile
            icon={Calculator}
            label="Financial Estimate"
            hint="See total tuition + monthly plan."
            to="/academy/finance"
            testId="parent-action-estimate"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)] gap-6">
          <div>
            {loading ? <div className="text-[#6E6E73]">Loading…</div> :
              error ? <div className="text-red-600 text-[14px]">{error}</div> :
                children.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[#E5E5E7] bg-white p-10 text-center">
                    <GraduationCap className="w-10 h-10 text-[#c9a227] mx-auto mb-3" />
                    <div className="text-[16px] font-bold text-[#1D1D1F]">No linked students</div>
                    <p className="text-[13.5px] text-[#6E6E73]">An admin needs to link your child's account to yours. Contact <a href="mailto:careers@zhelta.com" className="text-[#c9a227] hover:underline font-semibold">careers@zhelta.com</a>.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {children.map((c) => {
                      const outstanding = (c.invoices || []).filter((i) => (i.payment_status || '').toLowerCase() !== 'paid')
                        .reduce((acc, i) => acc + Number(i.amount || 0), 0);
                      const paid = (c.invoices || []).filter((i) => (i.payment_status || '').toLowerCase() === 'paid')
                        .reduce((acc, i) => acc + Number(i.amount || 0), 0);
                      return (
                        <div key={c.id} data-testid={`parent-child-${c.id}`}
                          className="rounded-2xl overflow-hidden border border-[#E5E5E7] bg-white">
                          <div className="px-6 py-4 bg-[#0e0e10] text-white flex items-center justify-between flex-wrap gap-3">
                            <div>
                              <div className="text-[18px] font-extrabold">{c.name || c.email}</div>
                              <div className="text-[12px] text-white/60">{c.email}</div>
                            </div>
                            <div className="flex items-center gap-3">
                              {outstanding > 0 && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider bg-amber-500/15 text-amber-300 border border-amber-400/30">
                                  <Wallet className="w-3 h-3" /> ${outstanding.toFixed(2)} due
                                </span>
                              )}
                              <span className="text-[10px] tracking-[0.22em] uppercase text-[#f5a623] font-bold">Student</span>
                            </div>
                          </div>

                          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <BookOpen className="w-4 h-4 text-[#c9a227]" />
                                <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Active courses</span>
                              </div>
                              {c.enrollments.length === 0 ? (
                                <div className="text-[13px] text-[#9ca3af]">No active courses.</div>
                              ) : (
                                <ul className="space-y-2">
                                  {c.enrollments.map((e) => (
                                    <li key={e.program_slug} className="rounded-lg border border-[#E5E5E7] px-3 py-2">
                                      <div className="font-bold text-[14px] text-[#1D1D1F]">{e.program_name}</div>
                                      <div className="text-[12px] text-[#6E6E73]">{e.tier_id} · {e.schedule || 'Self-paced'} · {e.progress || 0}%{e.grade ? ` · ${e.grade}` : ''}</div>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Receipt className="w-4 h-4 text-[#c9a227]" />
                                  <span className="text-[12px] font-bold tracking-[0.18em] uppercase text-[#6E6E73]">Recent invoices</span>
                                </div>
                                <span className="text-[10.5px] text-[#9aa0a6]">
                                  Paid <strong className="text-emerald-700">${paid.toFixed(0)}</strong>
                                </span>
                              </div>
                              {c.invoices.length === 0 ? (
                                <div className="text-[13px] text-[#9ca3af]">No invoices.</div>
                              ) : (
                                <ul className="space-y-1.5">
                                  {c.invoices.slice(0, 5).map((inv, idx) => (
                                    <li key={inv.session_id || idx} className="flex items-center justify-between gap-3 text-[12.5px]">
                                      <span className="text-[#6E6E73] truncate">{(inv.created_at || '').slice(0, 10)} · {inv.service_name}</span>
                                      <span className="font-bold text-[#1D1D1F] shrink-0">
                                        ${Number(inv.amount || 0).toFixed(2)}
                                        <span className={`ml-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${(inv.payment_status || '').toLowerCase() === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                          {inv.payment_status}
                                        </span>
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>

                          {/* Per-child quick actions */}
                          <div className="px-6 pb-5 flex flex-wrap gap-2">
                            <Link to="/academy/programs" data-testid={`parent-pay-${c.id}`}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider text-[#0e0e10]"
                              style={{ background: 'linear-gradient(90deg,#e6c35a 0%,#c9a227 100%)' }}>
                              <CreditCard className="w-3 h-3" /> Pay tuition
                            </Link>
                            <Link to="/academy/finance" data-testid={`parent-invoice-${c.id}`}
                              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11.5px] font-bold uppercase tracking-wider border border-[#0e0e10] text-[#0e0e10] hover:bg-[#0e0e10] hover:text-white transition-colors">
                              <FileText className="w-3 h-3" /> Request invoice
                            </Link>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

            <div className="mt-6">
              <Link to="/academy/support" className="text-[13px] text-[#c9a227] hover:underline font-semibold">Need help? Open a support ticket →</Link>
            </div>
          </div>

          {/* ----- Sidebar — counselor info for parents ----- */}
          <aside className="space-y-4">
            {counselors.length > 0 && (
              <div className="rounded-xl overflow-hidden border border-[#E5E5E7] bg-white"
                data-testid="parent-counselor-card">
                <div className="bg-[#0e0e10] text-white px-4 py-2.5 text-[13px] font-bold tracking-[0.18em] uppercase">
                  Family Advisor
                </div>
                <ul className="p-3 space-y-3">
                  {counselors.map((c) => (
                    <li key={c.id || c.email}
                      data-testid={`parent-counselor-${c.id || c.email}`}
                      className="rounded-lg border border-[#E5E5E7] p-3">
                      <div className="flex items-start gap-2.5">
                        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-[12px] font-extrabold text-[#0e0e10] tracking-wider"
                          style={{ background: 'linear-gradient(135deg,#fff8e7 0%,#ffe9a3 100%)' }}>
                          {initials(c.name)}
                        </span>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#a8871a] truncate">{c.role}</div>
                          <div className="text-[12.5px] font-extrabold text-[#0e0e10] leading-tight truncate">{c.name}</div>
                        </div>
                      </div>
                      {c.bio && <p className="text-[11.5px] text-[#6E6E73] mt-2 leading-snug">{c.bio}</p>}
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {c.email && (
                          <a href={`mailto:${c.email}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E5E5E7] text-[11px] font-bold text-[#0e0e10] hover:border-[#c9a227] hover:bg-[#fff8e7]">
                            <Mail className="w-3 h-3 text-[#c9a227]" /> Email
                          </a>
                        )}
                        {c.phone && (
                          <a href={`tel:${c.phone.replace(/[^+\d]/g, '')}`}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-[#E5E5E7] text-[11px] font-bold text-[#0e0e10] hover:border-[#c9a227] hover:bg-[#fff8e7]">
                            <Phone className="w-3 h-3 text-[#c9a227]" /> Call
                          </a>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="rounded-xl overflow-hidden border border-[#E5E5E7] bg-white">
              <div className="bg-[#0e0e10] text-white px-4 py-2.5 text-[13px] font-bold tracking-[0.18em] uppercase">
                My Information
              </div>
              <div className="p-4 space-y-1.5 text-[13px]">
                <div className="font-bold text-[#1D1D1F]">{user?.name || '—'}</div>
                <div className="text-[#6E6E73]">{user?.email}</div>
                <div className="text-[11px] uppercase tracking-wider text-[#c9a227] font-bold mt-2">{user?.role}</div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

function ParentActionTile({ icon: Icon, label, hint, to, testId, primary }) {
  return (
    <Link to={to} data-testid={testId}
      className={`group text-left rounded-xl border p-4 transition-all hover:-translate-y-0.5
        ${primary
          ? 'border-[#c9a227] bg-gradient-to-br from-[#fff8e7] to-white shadow-[0_10px_30px_-15px_rgba(201,162,39,0.45)]'
          : 'border-[#E5E5E7] bg-white hover:border-[#c9a227]/60 hover:shadow-[0_10px_30px_-18px_rgba(201,162,39,0.3)]'}`}>
      <span className={`inline-flex h-9 w-9 items-center justify-center rounded-xl mb-2.5 transition-transform group-hover:rotate-6
        ${primary ? 'bg-gradient-to-br from-[#e6c35a] to-[#c9a227] text-[#0e0e10]' : 'bg-[#fff8e7] text-[#a8871a]'}`}>
        <Icon className="w-4 h-4" />
      </span>
      <div className="text-[14px] font-extrabold text-[#0e0e10] group-hover:text-[#a8871a] transition-colors">{label}</div>
      <div className="text-[11.5px] text-[#6E6E73] mt-1 leading-snug">{hint}</div>
    </Link>
  );
}
